import {cdnImage} from '@/util/cdnImage';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, {memo} from 'react';
import DisplayImage from '../display-image';
const roboticImg = cdnImage('Latest-home-page/Service-left-img.webp');
const sendIcon = cdnImage('Latest-home-page/send-icon.webp');
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
const cardImg = cdnImage('Latest-home-page/service-card-img.webp');
import Link from 'next/link';
import parse from 'html-react-parser';
import {borderRadius, colors, fontSize, fontWeight, spacing, zIndex} from '@/styles/tokens';
import {nftButtonSx} from '@/styles/nftButtonSx';

// Absolute-positioned decorative image, only rendered behind the header on pages
// that pass `robotImage`. Migrated from globals.scss `.end-development-robot`.
const sxRobotImage = {
    position: 'absolute',
    width: '300px',
    height: 'auto',
    top: '-20px',
    left: '-176px',
    zIndex: -1
};

// Migrated from globals.scss `.end-service-card-wrapper`. The box-shadow is the
// stock Material elevation-1 value, but this repo's theme.shadows overrides the
// MUI default shadow scale (see src/theme/index.ts), so no tokens.shadows.* entry
// reproduces it — kept literal rather than mapped to a visually different token.
const sxServiceCard = {
    backgroundColor: colors.background.default,
    padding: '30px',
    maxWidth: '370px',
    borderRadius: borderRadius.sm,
    boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
};

// font-family intentionally omitted: globals.scss's `h1,h2,h3,h4,h5,h6` reset
// already forces 'Headers' on this element with !important, so re-declaring it
// here would be dead weight.
const sxServiceCardCta = {
    fontWeight: fontWeight.semibold,
    fontSize: fontSize[18],
    padding: '12px 0'
};

const sxCardWrapper = {
    position: 'relative'
};

// Migrated from globals.scss `.end-development-main-card`.
const sxMainCard = {
    backgroundColor: colors.background.subtle,
    position: 'relative'
};

// Migrated from globals.scss `.end-development-header-wrapper`.
const sxHeaderWrapper = {
    textAlign: 'left',
    maxWidth: '600px',
    position: 'sticky',
    top: '150px',
    zIndex: zIndex.elevated
};

// NOTE: the previous `& .nft-button` / `& .nft-button:hover` override here (background,
// border-color, hover color) set only non-`!important` declarations, so it could never
// actually beat the global `.nft-button` rule's `!important` declarations of the same
// properties — it was dead code. This button's real current rendering is the plain
// default `nftButtonSx` below; no override is carried forward, to avoid introducing a
// visual change (the "primary/midnight" look) that was never actually visible in production.

const sxCardHeading = {
    fontWeight: fontWeight.semibold,
    fontSize: fontSize[20],
    marginBottom: spacing[8],
    color: colors.text.primary
};

const sxCardLink = {
    textDecoration: 'none',
    '&:hover': {
        textDecoration: 'underline'
    }
};

const sxCardBody = {
    fontSize: fontSize[16]
};

// The 600px cutoff mirrors the removed `@media (max-width:600px)` override —
// MUI's default `sm` breakpoint is 600px, matching this repo's convention.
const sxCardImgWrapper = {
    overflow: 'hidden',
    '& img': {
        width: '100%',
        height: 'auto',
        clipPath: {xs: 'none', sm: 'polygon(19.77% 0, 100% 0, 100% 82.8%, 80.19% 100%, 0 100%, 0 17.11%)'},
        maxWidth: {xs: '150px', sm: 'none'}
    }
};

const sxCardBorder = {
    position: 'absolute',
    width: '72px',
    height: '72px',
    bottom: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
    backgroundColor: colors.primary.midnight,
    clipPath: 'polygon(127% 0, 0% 100%, 100% 100%)',
    padding: '0 6px 7px 0',
    zIndex: zIndex.overlay
};

const sxCardTopImg = {
    position: 'absolute',
    top: 0,
    right: 0
};

const sxCardInsideWrapper = {
    backgroundColor: colors.background.default,
    padding: spacing[24],
    clipPath: {xs: 'none', sm: 'polygon(8.2% 0, 100% 0, 100% 72%, 87.1% 100%, 0 100%, 0 17.5%)'},
    visibility: 'visible'
};

const EndDevelopment = (props: any) => {
    const {heading, subheading, contentData, Cta, robotImage = false, contactCard} = props;

    return (
        <Box className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding" sx={sxMainCard}>
            <MUIContainer>
                <Grid container spacing={2}>
                    <Grid item md={6} xs={12}>
                        <Box className="spectrum-header-wrapper" sx={sxHeaderWrapper}>
                            <Typography className="spectrum-heading color-black" component="h4" mb={3}>
                                {heading}
                            </Typography>
                            {subheading && (
                                <Box className="sub-heading-box" mb={3}>
                                    <Typography className="spectrum-sub-heading color-black ">
                                        {subheading ? parse(subheading) : ''}
                                    </Typography>
                                </Box>
                            )}
                            <Box sx={sxServiceCard}>
                                {contactCard ? (
                                    <DisplayImage alt="send icon" src={contactCard} />
                                ) : (
                                    <DisplayImage alt="send icon" src={sendIcon} />
                                )}

                                <Typography sx={sxServiceCardCta} component="h4">
                                    {Cta}
                                </Typography>
                                <MUIButton
                                    onClick={() => {
                                        globalThis.open('/contact-us', '_blank');
                                    }}
                                    className="animated-button"
                                    sx={nftButtonSx}
                                    variant="contained"
                                    label="Contact Us "
                                />
                            </Box>
                            {robotImage && (
                                <Box sx={sxRobotImage}>
                                    <DisplayImage alt="robotic Image" src={roboticImg} />
                                </Box>
                            )}
                        </Box>
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <Stack spacing={3}>
                            {contentData?.map((item: any, index: number) => (
                                <Box key={index + 1} sx={sxCardWrapper}>
                                    <Box sx={sxCardInsideWrapper}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <Box sx={sxCardImgWrapper}>
                                                    <DisplayImage alt={item.heading} src={item.src} />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                {item.redirect ? (
                                                    <Box component={Link} target="_blank" href={item.redirect} sx={sxCardLink}>
                                                        <Typography sx={sxCardHeading} component="h4">
                                                            {item.heading}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography sx={sxCardHeading} component="h4">
                                                        {item.heading}
                                                    </Typography>
                                                )}

                                                <Typography sx={sxCardBody} component="p">
                                                    {item.body ? parse(item.body) : ''}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Box sx={sxCardTopImg}>
                                            <DisplayImage src={cardImg} alt="card-img" />
                                        </Box>
                                    </Box>
                                    <Box sx={sxCardBorder} />
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(EndDevelopment);
